import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
  Divider,
  Avatar,
  useTheme,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  People as PeopleIcon,
} from "@mui/icons-material";
import { loginUser } from "../store/slices/authSlice";
import Logo from "../assets/ZakazujMeLogo4.png";
import { getTenantSubdomain } from "../utils/subdomain";
import tenantService from "../services/tenantService";
import { isTenantAdmin, isEmployee } from "../utils/userHelper";

const registrationStatusCache = new Map();

const normalizeValue = (value) => {
  if (!value) return "";
  return value.toString().trim().toLowerCase();
};

const extractTenantsFromResponse = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;

  const candidateArrays = [
    response.tenants,
    response.data,
    response.items,
    response.results,
    response.docs,
    response?.data?.tenants,
    response?.data?.items,
    response?.data?.results,
    response?.data?.docs,
  ];

  for (const candidate of candidateArrays) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  if (response.tenant) return [response.tenant];
  if (response.data?.tenant) return [response.data.tenant];

  return [];
};

const tenantMatchesSubdomain = (tenant, targetSubdomain) => {
  if (!tenant || !targetSubdomain) return false;
  const possibleValues = [
    tenant.subdomain,
    tenant.domain,
    tenant.slug,
    tenant.urlSlug,
    tenant?.settings?.subdomain,
  ];

  return possibleValues.some(
    (value) => normalizeValue(value) === targetSubdomain,
  );
};

const tenantAllowsRegistration = (tenant) => {
  if (!tenant) return false;
  const settings = tenant.settings || tenant;
  return Boolean(
    settings?.externalUserRegistration ||
    settings?.approvalOfExternalUserRegistration ||
    settings?.allowExternalUserRegistration,
  );
};

const interpretExternalRegistrationResponse = (response, targetSubdomain) => {
  if (!response) return false;

  const tenantCandidate =
    response?.tenant ||
    response?.data?.tenant ||
    response?.data ||
    response?.result ||
    null;

  if (
    tenantCandidate &&
    tenantMatchesSubdomain(tenantCandidate, targetSubdomain)
  ) {
    if (tenantAllowsRegistration(tenantCandidate)) {
      return true;
    }
  }

  const fallbackFlags = [
    response.allowExternalRegistration,
    response.externalUserRegistration,
    response.externalUserRegistrationEnabled,
    response.allowRegistration,
    response.registrationEnabled,
    response.isExternalRegistrationEnabled,
    response.externalUserRegistrationEnabled,
    response?.data?.allowExternalRegistration,
    response?.data?.externalUserRegistration,
    response?.data?.externalUserRegistrationEnabled,
    response?.data?.allowRegistration,
    response?.data?.registrationEnabled,
    response?.data?.isExternalRegistrationEnabled,
    response?.data?.externalUserRegistrationEnabled,
  ];

  return fallbackFlags.some(Boolean);
};

const HERO_IMG = null; //

const Login = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [isRegistrationAllowed, setIsRegistrationAllowed] = useState(false);
  const [isCheckingTenant, setIsCheckingTenant] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error } = useSelector((s) => s.auth);
  const theme = useTheme();
  const tenantSubdomain = useMemo(() => getTenantSubdomain(), []);
  const isTenantSpecificMode = Boolean(tenantSubdomain);

  useEffect(() => {
    let isMounted = true;

    const evaluateRegistrationStatus = async () => {
      const normalizedSubdomain = normalizeValue(tenantSubdomain);
      if (!normalizedSubdomain) {
        setIsRegistrationAllowed(false);
        setIsCheckingTenant(false);
        return;
      }

      if (registrationStatusCache.has(normalizedSubdomain)) {
        setIsRegistrationAllowed(
          registrationStatusCache.get(normalizedSubdomain),
        );
        setIsCheckingTenant(false);
        return;
      }

      setIsCheckingTenant(true);
      let allowed = false;

      try {
        const response =
          await tenantService.getExternalRegistrationInfo(normalizedSubdomain);
        allowed = interpretExternalRegistrationResponse(
          response,
          normalizedSubdomain,
        );
      } catch (error) {
        console.error("Failed to fetch external registration info:", error);
        try {
          const response = await tenantService.getTenantsByFilter({
            subdomain: normalizedSubdomain,
          });
          const tenants = extractTenantsFromResponse(response);
          const tenant = tenants.find((t) =>
            tenantMatchesSubdomain(t, normalizedSubdomain),
          );
          if (tenant) {
            allowed = tenantAllowsRegistration(tenant);
          }
        } catch (fallbackError) {
          console.error("Failed to fetch tenant by subdomain:", fallbackError);
        }
      }

      if (isMounted) {
        registrationStatusCache.set(normalizedSubdomain, allowed);
        setIsRegistrationAllowed(allowed);
        setIsCheckingTenant(false);
      }
    };

    evaluateRegistrationStatus();

    return () => {
      isMounted = false;
    };
  }, [tenantSubdomain]);

  const handleChange = (e) =>
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await dispatch(
      loginUser({ ...formData, subdomain: tenantSubdomain }),
    );
    if (loginUser.fulfilled.match(result)) {
      // Čitaj user objekat iz localStorage koji sadrži tenantConnections
      const userFromStorage = JSON.parse(localStorage.getItem("user") || "{}");
      const user = userFromStorage || result?.payload?.user;

      // Koristi helper funkcije koje proveravaju tenantConnections
      const userIsTenantAdmin = isTenantAdmin(user);
      const userIsEmployee = isEmployee(user);

      if (isTenantSpecificMode) {
        // Za tenant_admin i employee, idi na live-dashboard
        if (userIsTenantAdmin || userIsEmployee) {
          navigate("/live-dashboard");
        } else {
          navigate("/last-and-next-appointment");
        }
        return;
      }

      // Za tenant_admin i employee, idi na live-dashboard (po defaultu)
      if (userIsTenantAdmin || userIsEmployee) {
        navigate("/live-dashboard");
        return;
      }

      // Za customer korisnike, proveri da li imaju tenantConnections
      const tenantConnections = user?.tenantConnections || [];
      const hasSingleApprovedConnection =
        tenantConnections.length === 1 &&
        tenantConnections[0]?.approved &&
        tenantConnections[0]?.tenantId;

      if (hasSingleApprovedConnection) {
        navigate("/last-and-next-appointment");
      } else {
        navigate("/homepage");
      }
    }
  };

  const forgotPassword = async (e) => {
    e.preventDefault();
    navigate("/forgotpassword");
  };

  return (
    <Grid
      container
      sx={{
        minHeight: "100vh",
        overflow: "hidden",
        justifyContent: isTenantSpecificMode ? "center" : "flex-start",
        alignItems: isTenantSpecificMode ? "center" : "stretch",
        background: isTenantSpecificMode
          ? theme.palette.mode === "dark"
            ? "linear-gradient(180deg, #0B1020 0%, #101738 100%)"
            : "linear-gradient(180deg, #0F1431 0%, #171B46 100%)"
          : "transparent",
        py: isTenantSpecificMode ? 6 : 0,
        px: isTenantSpecificMode ? 2 : 0,
      }}
    >
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 1,
          px: 2,
          py: 1,
          mt: 2,
          borderRadius: 2,
          background: "rgba(10, 13, 32, 0.6)",
          color: "rgba(255,255,255,0.95)",
          width: "100%",
        }}
      >
        <Link
          underline="none"
          color="inherit"
          component={RouterLink}
          to="/home"
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
            fontWeight: 700,
            fontSize: 14,
            transition: "background 0.2s, color 0.2s",
            "&:hover": {
              background: "rgba(59,130,246,0.15)",
              color: "#3B82F6",
            },
          }}
        >
          Početna
        </Link>
        <Link
          underline="none"
          color="inherit"
          component={RouterLink}
          to="/cooperation"
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
            fontWeight: 700,
            fontSize: 14,
            transition: "background 0.2s, color 0.2s",
            "&:hover": {
              background: "rgba(59,130,246,0.15)",
              color: "#3B82F6",
            },
          }}
        >
          Saradnja
        </Link>
        <Link
          underline="none"
          color="inherit"
          component={RouterLink}
          to="/about"
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
            fontWeight: 700,
            fontSize: 14,
            transition: "background 0.2s, color 0.2s",
            "&:hover": {
              background: "rgba(59,130,246,0.15)",
              color: "#3B82F6",
            },
          }}
        >
          O nama
        </Link>
        <Link
          underline="none"
          color="inherit"
          component={RouterLink}
          to="/privacy-policy"
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
            fontWeight: 700,
            fontSize: 14,
            transition: "background 0.2s, color 0.2s",
            "&:hover": {
              background: "rgba(59,130,246,0.15)",
              color: "#3B82F6",
            },
          }}
        >
          Politika privatnosti
        </Link>
        <Link
          underline="none"
          color="inherit"
          component={RouterLink}
          to="/login"
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
            fontWeight: 700,
            fontSize: 14,
            transition: "background 0.2s, color 0.2s",
            "&:hover": {
              background: "rgba(59,130,246,0.15)",
              color: "#3B82F6",
            },
          }}
        >
          Prijavi se
        </Link>
      </Box>
      <Grid
        item
        xs={12}
        md={isTenantSpecificMode ? 8 : 5}
        lg={isTenantSpecificMode ? 6 : 4}
        sx={{
          position: "relative",
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(180deg, #0B1020 0%, #101738 100%)"
              : "linear-gradient(180deg, #0F1431 0%, #171B46 100%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center", // center horizontally
          justifyContent: isTenantSpecificMode ? "center" : "flex-start",
          px: { xs: 3, sm: 5 },
          py: { xs: 4, sm: 6 },
          boxShadow: { md: "8px 0 28px rgba(0,0,0,0.35)" },
          zIndex: 2,
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 520,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mt: { xs: 2, sm: 3 },
          }}
        >
          <Box
            component="img"
            src={Logo}
            alt="Zakazuj"
            sx={{
              height: { xs: 140, sm: 180, md: "clamp(160px, 22vh, 240px)" },
              objectFit: "contain",
              filter: "drop-shadow(0 0 18px rgba(124,58,237,0.6))",
              mb: 2,
              "@media (max-height: 800px)": { height: 140 },
            }}
          />

          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: 3,
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)",
              border: "1px solid rgba(255,255,255,0.1)",
              backdropFilter: "blur(8px)",
              color: "common.white",
              width: "100%",
              maxWidth: 480,
            }}
          >
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.2)",
                width: 56,
                height: 56,
                mb: 2,
              }}
            >
              <PeopleIcon />
            </Avatar>

            <Typography variant="h4" fontWeight={800} sx={{ mb: 0.5 }}>
              Uloguj Se
            </Typography>
            {/* <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.75)", mb: 3 }}
          >
            Super Admin Login
          </Typography> */}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" noValidate onSubmit={handleSubmit}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="E-mail Adresa"
                name="email"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleChange}
                disabled={isLoading}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "common.white",
                    borderRadius: 999,
                    bgcolor: "rgba(255,255,255,0.06)",
                    "& fieldset": { borderColor: "rgba(255,255,255,0.18)" },
                    "&:hover fieldset": {
                      borderColor: "rgba(255,255,255,0.35)",
                    },
                  },
                  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
                }}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                id="password"
                name="password"
                label="Lozinka"
                type={showPwd ? "text" : "password"}
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                disabled={isLoading}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "common.white",
                    borderRadius: 999,
                    bgcolor: "rgba(255,255,255,0.06)",
                    "& fieldset": { borderColor: "rgba(255,255,255,0.18)" },
                    "&:hover fieldset": {
                      borderColor: "rgba(255,255,255,0.35)",
                    },
                  },
                  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
                }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPwd((s) => !s)}
                        edge="end"
                        sx={{ color: "rgba(255,255,255,0.8)" }}
                      >
                        {showPwd ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                  mt: 2,
                  py: 1.25,
                  borderRadius: 999,
                  fontWeight: 700,
                  textTransform: "none",
                  background:
                    "linear-gradient(90deg, #3B82F6 0%, #1D4ED8 48%, #06B6D4 100%)",
                  boxShadow: "0 12px 28px rgba(124, 58, 237, 0.35)",
                  "&:hover": {
                    boxShadow: "0 16px 36px rgba(124, 58, 237, 0.55)",
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={30} sx={{ color: "white" }} />
                ) : (
                  "Prijavi se"
                )}
              </Button>

              <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.12)" }} />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ flex: 1, textAlign: "left" }}
                >
                  Unesi svoje kredencijale za pristup portalu.
                </Typography>
                <Link
                  onClick={forgotPassword}
                  component={RouterLink}
                  to="/forgot-password"
                  underline="hover"
                  color="inherit"
                  sx={{ minWidth: 0, textAlign: "right", ml: 2 }}
                >
                  Zaboravljena lozinka?
                </Link>
                {isTenantSpecificMode &&
                  isRegistrationAllowed &&
                  !isCheckingTenant && (
                    <Link
                      component={RouterLink}
                      to="/userregistration"
                      underline="hover"
                      color="inherit"
                      sx={{
                        minWidth: 0,
                        textAlign: "right",
                        ml: 2,
                        fontWeight: 600,
                      }}
                    >
                      Registruj se
                    </Link>
                  )}
              </Box>
              <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.12)" }} />
              {/* <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ flex: 1, textAlign: "left" }}
                >
                  Nemate korisnicki nalog?
                </Typography>
                <Link
                  component={RouterLink}
                  to="/userregistration"
                  underline="hover"
                  color="inherit"
                  sx={{ minWidth: 0, textAlign: "right", ml: 2 }}
                >
                  Napravi nalog
                </Link>
              </Box> */}
            </Box>
          </Paper>
        </Box>

        <Box sx={{ pt: 4, color: "rgba(255,255,255,0.55)" }}>
          <Typography variant="caption">
            Zakazuj © {new Date().getFullYear()}
          </Typography>
        </Box>
      </Grid>

      {!isTenantSpecificMode && (
        <Grid
          item
          xs={12}
          md={7}
          lg={8}
          sx={{
            position: "relative",
            display: { xs: "none", md: "block" },
            overflow: "hidden",
            background: HERO_IMG
              ? `url(${HERO_IMG}) center/cover no-repeat`
              : "radial-gradient(1200px 800px at 20% 30%, #1e2a78 0%, #0b0f2b 50%, #0a0d20 100%)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(600px 380px at 45% 40%, rgba(26, 86, 219, 0.65), transparent 60%), radial-gradient(620px 420px at 65% 60%, rgba(168, 85, 247, 0.55), transparent 62%), radial-gradient(580px 360px at 75% 30%, rgba(6, 182, 212, 0.5), transparent 62%)",
              filter: "blur(18px)",
              opacity: 0.9,
            }}
          />

          <Box
            sx={{
              position: "absolute",
              left: { md: 48, lg: 72 },
              bottom: { md: 56, lg: 84 },
              color: "white",
              textShadow: "0 8px 24px rgba(0,0,0,0.45)",
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 900,
                lineHeight: 1.05,
                letterSpacing: "-0.02em",
                mb: 0.5,
              }}
            >
              Dobrodošli na Vašu
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 900,
                lineHeight: 1.05,
                letterSpacing: "-0.02em",
                background:
                  "linear-gradient(90deg, #ffffff 0%, #3B82F6 30%, #1D4ED8 60%, #06B6D4 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              omiljenu platformu za zakazivanje.
            </Typography>
          </Box>

          <Box
            sx={{
              position: "absolute",
              top: 18,
              right: 24,
              display: "flex",
              gap: 2,
              color: "rgba(255,255,255,0.95)",
              fontSize: 18,
            }}
          >
            <Link
              underline="none"
              color="inherit"
              component={RouterLink}
              to="/home"
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: 25,
                transition: "background 0.2s, color 0.2s",
                "&:hover": {
                  background: "rgba(59,130,246,0.15)",
                  color: "#3B82F6",
                },
              }}
            >
              Početna
            </Link>
            <Link
              underline="none"
              color="inherit"
              component={RouterLink}
              to="/cooperation"
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: 25,
                transition: "background 0.2s, color 0.2s",
                "&:hover": {
                  background: "rgba(59,130,246,0.15)",
                  color: "#3B82F6",
                },
              }}
            >
              Saradnja
            </Link>
            <Link
              underline="none"
              color="inherit"
              component={RouterLink}
              to="/about"
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: 25,
                transition: "background 0.2s, color 0.2s",
                "&:hover": {
                  background: "rgba(59,130,246,0.15)",
                  color: "#3B82F6",
                },
              }}
            >
              O nama
            </Link>
            <Link
              underline="none"
              color="inherit"
              component={RouterLink}
              to="/privacy-policy"
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: 25,
                transition: "background 0.2s, color 0.2s",
                "&:hover": {
                  background: "rgba(59,130,246,0.15)",
                  color: "#3B82F6",
                },
              }}
            >
              Politika Privatnosti
            </Link>
            <Link
              underline="none"
              color="inherit"
              component={RouterLink}
              to="#"
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: 25,
                transition: "background 0.2s, color 0.2s",
                "&:hover": {
                  background: "rgba(59,130,246,0.15)",
                  color: "#3B82F6",
                },
              }}
            >
              Prijavi se
            </Link>
            <Link
              hidden
              underline="none"
              color="inherit"
              component={RouterLink}
              to="/userregistration"
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: 25,
                background: "linear-gradient(90deg, #3B82F6 0%, #06B6D4 100%)",
                color: "white",
                boxShadow: "0 2px 8px rgba(59,130,246,0.25)",
                transition: "background 0.2s, color 0.2s",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, #1D4ED8 0%, #06B6D4 100%)",
                  color: "#fff",
                },
              }}
            >
              Registruj se
            </Link>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default Login;
